<template>
  <div class="space-y-4">
    <div class="space-y-2">
      <label for="tco-cardholder-name" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
        Cardholder name
      </label>
      <input
        id="tco-cardholder-name"
        v-model="nameField"
        type="text"
        autocomplete="cc-name"
        class="block w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
        placeholder="Name on card"
        @focus="onNameFocus"
        @blur="onNameBlur"
      />
    </div>

    <div class="space-y-2">
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
        Card details
      </label>
      <div id="tco-card-element">
        <!--
          2Pay.js iframe mounts here. Container is intentionally unstyled
          (no min-h, no border, no padding) so the iframe self-sizes to its
          content + inherits surrounding spacing. Styling lives inside the
          iframe via cardStyle.ts.
        -->
      </div>
    </div>

    <div v-if="mountError" class="text-sm text-red-600 dark:text-red-400">
      {{ mountError }}
    </div>

    <div class="text-xs text-gray-500 dark:text-gray-400">
      Payment card data is collected by 2Checkout (Verifone) in a secure
      iframe. We never see your card number.
    </div>
  </div>
</template>

<script setup lang="ts">
  import { onBeforeUnmount, onMounted, ref } from 'vue';
  import { useTwoPay } from '../composables/useTwoPay';
  import { cardStyle } from '../composables/cardStyle';

  interface Props {
    /** Dark mode flag — selects the dark theme variant. */
    dark?: boolean;
    /** ISO-639 language code for iframe labels. */
    lang?: string;
  }
  const props = withDefaults(defineProps<Props>(), { dark: false, lang: 'en' });

  const selector = '#tco-card-element';
  // cardStyle() returns the full 2CO default rule-set with our color tweaks
  // layered on top. 2Pay.js replaces (not merges) the default, so passing a
  // sparse object breaks the iframe layout.
  const { preload, mount, tokenize, unmount } = useTwoPay({
    lang: props.lang,
    style: cardStyle(props.dark),
  });

  const nameField = ref('');
  const nameFocusAt = ref<number | null>(null);
  const holderNameTime = ref(0);
  const mountError = ref('');

  function onNameFocus() {
    nameFocusAt.value = Date.now();
  }
  function onNameBlur() {
    if (nameFocusAt.value) {
      holderNameTime.value = Number(((Date.now() - nameFocusAt.value) / 1000).toFixed(2));
    }
  }

  onMounted(async () => {
    // Surface a dev-friendly message before attempting to load 2Pay.js.
    const code = import.meta.env.VITE_TCO_MERCHANT_CODE;
    if (!code) {
      mountError.value =
        'VITE_TCO_MERCHANT_CODE is empty. Set it in .env.development (or .env.local) and restart `npm run dev`.';
      return;
    }
    try {
      // Preload immediately so the modal open → mount transition is instant.
      await preload(selector);
      await mount(selector);
    } catch (e: unknown) {
      mountError.value = (e as Error).message ?? 'Failed to load payment form';
    }
  });

  onBeforeUnmount(() => {
    unmount();
  });

  // Exposed to parent. Throws on 2Pay.js error; caller handles.
  async function submit(): Promise<{ token: string; cardBrand?: string; name: string }> {
    if (!nameField.value.trim()) {
      throw new Error('Cardholder name required');
    }
    const resp = await tokenize({
      name: nameField.value.trim(),
      holderNameTime: holderNameTime.value,
      scope: 'subscription',
    });
    return { token: resp.token, cardBrand: resp.cardBrand, name: nameField.value.trim() };
  }

  defineExpose({ submit });
</script>

<style scoped>
  /*
   * 2Pay.js mounts its <iframe> inside #tco-card-element. The library sets
   * a fixed height on the iframe that is occasionally a few pixels shorter
   * than its internal document, producing an internal scrollbar. Since the
   * iframe is cross-origin we can't reach inside to fix it; instead we
   * size the iframe element itself so the native height always exceeds the
   * internal document.
   *
   * `:deep(...)` is Vue's scoped-CSS escape hatch so the selector matches
   * the iframe element injected by 2Pay.js.
   */
  :deep(#tco-card-element) {
    display: block;
    width: 100%;
  }
  :deep(#tco-card-element iframe) {
    width: 100% !important;
    /*
     * 2Pay.js's default style renders 2 rows (card number + exp/cvv) at
     * ~180px. Use height:auto so the iframe follows 2Pay.js's own sizing
     * event; min-height acts as a floor during the first mount before
     * sizing settles.
     */
    height: auto !important;
    min-height: 190px;
    border: 0 !important;
    display: block;
    overflow: hidden;
  }
</style>
